/**
 * Operacao de margem na averbacao do banco, e as formas que o cartao traz
 * (ADR-0014).
 *
 * Os BALDES de margem continuam tres (`TipoMargem`). A operacao e' balde x
 * forma: o emprestimo desconta parcela fixa; o cartao se divide em RESERVA, que
 * compromete a margem, e LANCAMENTO, que e' o valor de uma competencia.
 */
import { z } from "zod";
export const OperacaoMargemSchema = z.enum([
    "EMPRESTIMO",
    "LANCAMENTO_CARTAO_CONSIGNADO",
    "LANCAMENTO_CARTAO_BENEFICIOS",
    "RESERVA_CARTAO_CONSIGNADO",
    "RESERVA_CARTAO_BENEFICIOS",
]);
/** As cinco, na ordem em que a tela do banco as oferece. */
export const OPERACOES_MARGEM = OperacaoMargemSchema.options;
/** O rotulo de cada operacao — o texto que a tela do banco mostra. */
export const ROTULO_OPERACAO_MARGEM = {
    EMPRESTIMO: "Empréstimo consignado",
    LANCAMENTO_CARTAO_CONSIGNADO: "Lançamento cartão crédito",
    LANCAMENTO_CARTAO_BENEFICIOS: "Lançamento cartão benefício",
    RESERVA_CARTAO_CONSIGNADO: "Reserva cartão crédito",
    RESERVA_CARTAO_BENEFICIOS: "Reserva cartão benefício",
};
/** Os dois baldes que tem reserva e lancamento. */
export const ProdutoCartaoSchema = z.enum(["CARTAO_CONSIGNADO", "CARTAO_BENEFICIOS"]);
/** O balde de margem que a operacao ocupa. */
export function baldeDaOperacao(op) {
    if (op === "EMPRESTIMO")
        return "EMPRESTIMO";
    return op.endsWith("_BENEFICIOS") ? "CARTAO_BENEFICIOS" : "CARTAO_CONSIGNADO";
}
export function operacaoEhReserva(op) {
    return op.startsWith("RESERVA_");
}
export function operacaoEhLancamento(op) {
    return op.startsWith("LANCAMENTO_");
}
/** A operacao de reserva ou de lancamento de um produto cartao. */
export function operacaoDoCartao(forma, produto) {
    return `${forma === "reserva" ? "RESERVA" : "LANCAMENTO"}_${produto}`;
}
/** Estado de um lancamento: `informado` vai no movimento; `cancelado` nao. */
export const EstadoLancamentoCartaoSchema = z.enum(["informado", "cancelado"]);
/**
 * As condicoes do contrato que o banco digita — as MESMAS do emprestimo
 * (05/10/2026): a reserva e cada lancamento passam pelo mesmo formulario.
 */
export const CondicoesCartaoSchema = z.object({
    numeroContratoBanco: z.string().nullable().optional(),
    vencimento: z.string().nullable().optional(),
    dataLiberacao: z.string().nullable().optional(),
    valorFinanciado: z.number().nullable().optional(),
    valorLiquido: z.number().nullable().optional(),
    iof: z.number().nullable().optional(),
    jurosAm: z.number().nullable().optional(),
    cetAm: z.number().nullable().optional(),
    diasCarencia: z.number().int().nullable().optional(),
    valorCarencia: z.number().nullable().optional(),
    observacoes: z.string().nullable().optional(),
});
export const LancamentoCartaoSchema = CondicoesCartaoSchema.extend({
    reservaAdf: z.string(),
    /** AAAAMM. */
    competencia: z.string().regex(/^\d{6}$/),
    valor: z.number(),
    /** Que parcela da reserva este lancamento e' (1-based). Nulo no cancelado. */
    parcelaNumero: z.number().int().nullable(),
    estado: EstadoLancamentoCartaoSchema,
    /** A competencia ja' passou do corte ou a folha fechou: nao muda mais. */
    congelado: z.boolean(),
    versao: z.number().int(),
    atualizadoEm: z.string(),
    /** O que a folha fez com ele, quando o retorno ja' chegou. */
    resultado: z.string().nullable().optional(),
    valorDescontado: z.number().nullable().optional(),
});
export const ReservaCartaoSchema = z.object({
    adf: z.string(),
    produto: ProdutoCartaoSchema,
    /** O que a reserva ocupa na margem: o balde inteiro (14/10/2026). */
    valorReservado: z.number(),
    /** A parcela contratada — teto de cada lancamento. Reserva anterior a
     *  14/10/2026 nao tem: vem igual ao reservado. */
    valorParcela: z.number(),
    numeroContratoBanco: z.string().nullable(),
    /** O prazo da reserva, em parcelas. 0 = sem prazo (reserva anterior ao prazo). */
    parcelasTotal: z.number().int(),
    /** Parcelas ja' lancadas (lancamentos informados). */
    parcelasLancadas: z.number().int(),
    /** A proxima a lancar. Nulo quando todas ja' foram lancadas. */
    parcelaVigente: z.number().int().nullable(),
    /** Quantas faltam lancar. Nulo na reserva sem prazo. */
    parcelasFaltam: z.number().int().nullable(),
    situacao: z.string(),
    convenioId: z.string(),
    /** AAAAMM a partir da qual aceita lancamento. */
    competenciaInicial: z.string(),
    lancamentos: z.array(LancamentoCartaoSchema),
});
/** Competencia que ainda aceita lancamento, com o instante do corte. */
export const CompetenciaLancamentoSchema = z.object({
    competencia: z.string().regex(/^\d{6}$/),
    /** ISO com offset. */
    corteEm: z.string(),
});
//# sourceMappingURL=cartao.js.map